import { useNavigate } from "@tanstack/react-router";
import type { Uuid } from "spacetimedb";

import { CompletedActionCard } from "@/web/routes/_protected/smb/-dashboard/-CompletedActionCard";
import { shareableCardUrl } from "@/web/routes/_protected/smb/-dashboard/-actions/share/ShareBusinessCardAction";
import { useSelectedBrandIndex } from "@/web/routes/_protected/smb/-dashboard/brandSelection";
import type { SmbActionDefinition } from "@/web/routes/_protected/smb/-dashboard/-types";

type Props = {
  /** The card whose published page this completed card reflects. Drives the
   * public link at `/ttw/<cardId>`. */
  cardId: Uuid;
  /** Whether the card is password-protected. When true, the public link is
   * labeled as protected (and a note explains the access code is required), so
   * this card stays consistent with the protected save toast rather than
   * implying the page is openly viewable. */
  requiresAccessCode: boolean;
};

/**
 * Completed-side card for the classical TTW page. Appears once a page exists for
 * the brand's card. The primary action reopens the editor; the Details face
 * links out to the public page — labeled as protected when the card has a
 * password.
 */
function TtwPageCompleted({ cardId, requiresAccessCode }: Props) {
  const navigate = useNavigate();
  // Carry the dashboard's selected brand into the composer — without it a
  // multi-brand owner would edit their FIRST brand's page from any brand's deck.
  const brand = useSelectedBrandIndex();

  return (
    <CompletedActionCard
      category="fundraise"
      badge="Fundraise"
      title="Your investor page"
      action={{
        label: "Edit",
        onClick: () => navigate({ to: "/profile/ttw-editor", search: { brand } }),
      }}
      details={{
        title: "Your investor page",
        children: (
          <div className="flex flex-col gap-1">
            <a
              href={shareableCardUrl(cardId)}
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-2"
            >
              {requiresAccessCode ? "View protected page →" : "View live page →"}
            </a>
            {requiresAccessCode ? (
              <p className="text-xs text-muted-foreground">
                Your card's password is required to view this page. Share the password with anyone
                you send the link to.
              </p>
            ) : null}
          </div>
        ),
      }}
    />
  );
}

export function createTtwPageCompleted(props: Props & { id?: string }): SmbActionDefinition {
  const { cardId, requiresAccessCode, id = `ttw-page:done:${cardId.toString()}` } = props;
  return {
    id,
    category: "fundraise",
    canSkip: false,
    render: () => <TtwPageCompleted cardId={cardId} requiresAccessCode={requiresAccessCode} />,
  };
}
